import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useFeatures } from '../hooks/useFeatures';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { 
  Eye, Warning, Lightbulb, CaretCircleRight, Clock, Target, Info,
  Play, Pause, ArrowLeft, Trophy, CaretRight, Mouse, CheckCircle,
  XCircle, SpinnerGap, VideoCamera
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hazard Perception Video Player
const HazardPlayer = ({ clip, onComplete, onBack }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [clicks, setClicks] = useState([]);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const [scoring, setScoring] = useState(false);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleVideoEnd = useCallback(() => {
    setPlaying(false);
    setFinished(true);
  }, []);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const handleClick = (e) => {
    if (!playing || finished) return;
    
    // Record click timestamp
    const clickTime = videoRef.current?.currentTime || 0;
    setClicks(prev => [...prev, clickTime]);

    // Show click indicator
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const indicator = document.createElement('div');
      indicator.className = 'hazard-click-indicator';
      indicator.style.left = `${e.clientX - rect.left}px`;
      indicator.style.top = `${e.clientY - rect.top}px`;
      containerRef.current.appendChild(indicator);
      setTimeout(() => indicator.remove(), 600);
    }
  };

  const handleScore = async () => {
    setScoring(true);
    try {
      const { data } = await axios.post(`${API}/hazard-clips/${clip.clip_id}/score`, {
        clicks,
      });
      setResult(data);
    } catch (err) {
      console.error('Scoring failed:', err);
    } finally {
      setScoring(false);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="max-w-5xl mx-auto" data-testid="hazard-player">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-2 hover:bg-muted rounded-sm transition-colors" data-testid="hazard-back-btn">
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-lg">{clip.title}</h2>
          <p className="text-xs text-muted-foreground">
            Click when you spot a developing hazard  •  {clip.hazard_count} hazard{clip.hazard_count !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Video Container */}
      <div
        ref={containerRef}
        className="relative bg-black rounded-sm overflow-hidden cursor-crosshair mb-4"
        onClick={handleClick}
        data-testid="hazard-video-container"
      >
        <video
          ref={videoRef}
          src={clip.video_url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
          onEnded={handleVideoEnd}
          className="w-full aspect-video"
          playsInline
          data-testid="hazard-video"
        />

        {/* Play overlay */}
        {!playing && !finished && currentTime === 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); handlePlay(); }}
            className="absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity hover:bg-black/30"
            data-testid="hazard-play-overlay"
          >
            <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center">
              <Play weight="fill" className="w-10 h-10 text-primary-foreground ml-1" />
            </div>
          </button>
        )}

        {/* Click counter */}
        {playing && (
          <div className="absolute top-3 right-3 bg-black/60 text-white px-3 py-1.5 rounded-sm text-xs font-mono">
            <Mouse weight="fill" className="w-3 h-3 inline mr-1" />
            {clicks.length} click{clicks.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-muted rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-200"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-muted-foreground font-mono">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
        <div className="flex gap-2">
          {!finished ? (
            <Button
              size="sm"
              variant="outline"
              onClick={playing ? handlePause : handlePlay}
              className="rounded-sm"
              data-testid="hazard-play-pause"
            >
              {playing ? <Pause weight="fill" className="w-4 h-4 mr-1" /> : <Play weight="fill" className="w-4 h-4 mr-1" />}
              {playing ? 'Pause' : 'Play'}
            </Button>
          ) : !result ? (
            <Button onClick={handleScore} disabled={scoring} className="rounded-sm" data-testid="hazard-score-btn">
              {scoring ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <Trophy weight="fill" className="w-4 h-4 mr-2" />}
              See Results
            </Button>
          ) : (
            <Button onClick={() => onComplete(result)} className="rounded-sm" data-testid="hazard-continue-btn">
              <CaretRight weight="bold" className="w-4 h-4 mr-1" />
              Continue
            </Button>
          )}
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="bg-card border border-border rounded-sm p-6 animate-slide-in" data-testid="hazard-results">
          <div className="flex items-center gap-3 mb-4">
            <Trophy weight="fill" className="w-6 h-6 text-primary" />
            <h3 className="font-heading font-bold text-xl">
              Score: {result.total_score} / {result.max_score}
            </h3>
          </div>

          <div className="space-y-3">
            {result.hazard_results.map((h, i) => (
              <div key={i} className={cn(
                "flex items-center justify-between p-3 rounded-sm border",
                h.score > 0 ? "bg-success/10 border-success/30" : "bg-destructive/10 border-destructive/30"
              )}>
                <div className="flex items-center gap-2">
                  {h.score > 0 ? (
                    <CheckCircle weight="fill" className="w-5 h-5 text-success" />
                  ) : (
                    <XCircle weight="fill" className="w-5 h-5 text-destructive" />
                  )}
                  <span className="text-sm">
                    Hazard {i + 1}{h.description ? `: ${h.description}` : ''}
                  </span>
                </div>
                <span className="font-mono font-bold text-sm">{h.score}/5</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const HazardPerceptionPage = () => {
  const navigate = useNavigate();
  const { hasFeature, pricing, loading: featuresLoading } = useFeatures();
  const [clips, setClips] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeClip, setActiveClip] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clipsRes, statsRes] = await Promise.all([
        axios.get(`${API}/hazard-clips`, { withCredentials: true }),
        axios.get(`${API}/hazard-clips/stats/summary`, { withCredentials: true })
      ]);
      setClips(clipsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        // Feature locked
        setClips([]);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const tips = [
    { icon: Eye, text: "Click as soon as you spot a developing hazard" },
    { icon: Clock, text: "Don't click too early or too late — timing is key" },
    { icon: Target, text: "One clip may have two hazards, the rest have one" },
    { icon: Lightbulb, text: "You can score up to 5 points per hazard" },
    { icon: CaretCircleRight, text: "Practice identifying hazards that could cause you to change speed or direction" }
  ];

  const hazardTypes = [
    "Pedestrians stepping into the road",
    "Vehicles emerging from junctions",
    "Cyclists moving into your path",
    "Road conditions changing",
    "Animals on or near the road",
    "Children playing near the road",
    "Vehicles pulling out from parking spaces",
    "Motorcyclists filtering through traffic"
  ];

  // Active clip player mode
  if (activeClip) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="hazard-perception-page">
        <HazardPlayer
          clip={activeClip}
          onBack={() => { setActiveClip(null); fetchData(); }}
          onComplete={() => { setActiveClip(null); fetchData(); }}
        />
      </div>
    );
  }

  if (!featuresLoading && !hasFeature('hazard_perception')) {
    return <UpgradePrompt feature="hazard_perception" requiredTier="premium" pricing={pricing} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const hasClips = clips.length > 0;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="hazard-perception-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Test Component
          </p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            Hazard Perception
          </h1>
        </div>

        {/* Info Card */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
              <Info weight="fill" className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl mb-2">About the Test</h2>
              <p className="text-muted-foreground text-sm">
                The hazard perception test is part of the UK theory test. You'll watch 14 video clips 
                and need to identify developing hazards by clicking the mouse.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-4 bg-muted rounded-sm">
              <p className="font-heading font-black text-2xl text-primary">14</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Video Clips</p>
            </div>
            <div className="p-4 bg-muted rounded-sm">
              <p className="font-heading font-black text-2xl text-primary">15</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Hazards</p>
            </div>
            <div className="p-4 bg-muted rounded-sm">
              <p className="font-heading font-black text-2xl text-primary">44</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Pass Mark</p>
            </div>
            <div className="p-4 bg-muted rounded-sm">
              <p className="font-heading font-black text-2xl text-primary">75</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Max Score</p>
            </div>
          </div>
        </div>

        {/* DVSA Official Guide Video */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8" data-testid="dvsa-guide-section">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
              <VideoCamera weight="fill" className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl mb-1">Official DVSA Guide</h2>
              <p className="text-muted-foreground text-sm">
                Watch the official DVSA hazard perception guide before you start practising.
              </p>
            </div>
          </div>
          <div className="rounded-sm overflow-hidden bg-black">
            <video
              src="https://assets.testready.co.uk/guides/de4dfd4e21324bf0af4a0d04e40e12eb.mp4"
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-video"
              data-testid="dvsa-guide-video"
            />
          </div>
        </div>

        {/* User Stats */}
        {stats && stats.total_attempts > 0 && (
          <div className="bg-card border border-border rounded-sm p-6 mb-8" data-testid="hazard-stats">
            <h2 className="font-heading font-bold text-xl mb-4">Your Progress</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-4 bg-muted rounded-sm">
                <p className="font-heading font-black text-2xl text-primary">{stats.total_attempts}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Attempts</p>
              </div>
              <div className="p-4 bg-muted rounded-sm">
                <p className="font-heading font-black text-2xl text-primary">{stats.average_score}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg Score</p>
              </div>
              <div className="p-4 bg-muted rounded-sm">
                <p className="font-heading font-black text-2xl text-primary">{stats.best_score}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Best Score</p>
              </div>
              <div className="p-4 bg-muted rounded-sm">
                <p className="font-heading font-black text-2xl text-primary">{stats.clips_practiced}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Clips Done</p>
              </div>
            </div>
          </div>
        )}

        {/* Practice Clips */}
        {hasClips && (
          <div className="bg-card border border-border rounded-sm p-6 mb-8" data-testid="hazard-clips-list">
            <h2 className="font-heading font-bold text-xl mb-4">Practice Clips</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {clips.map((clip) => (
                <button
                  key={clip.clip_id}
                  onClick={() => setActiveClip(clip)}
                  className="group flex items-start gap-4 p-4 bg-muted rounded-sm hover:bg-muted/80 transition-colors text-left"
                  data-testid={`hazard-clip-${clip.clip_id}`}
                >
                  <div className="w-16 h-12 bg-black rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {clip.thumbnail_url ? (
                      <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <VideoCamera weight="fill" className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm group-hover:text-primary transition-colors">{clip.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {clip.hazard_count} hazard{clip.hazard_count !== 1 ? 's' : ''}
                      {clip.duration ? ` • ${formatTime(clip.duration)}` : ''}
                    </p>
                  </div>
                  <Play weight="fill" className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <h2 className="font-heading font-bold text-xl mb-4">Tips for Success</h2>
          <div className="space-y-3">
            {tips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-sm">
                  <Icon weight="fill" className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-sm">{tip.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hazard Types */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <h2 className="font-heading font-bold text-xl mb-4">Common Hazard Types</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {hazardTypes.map((hazard, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-muted rounded-sm">
                <Warning weight="fill" className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm">{hazard}</span>
              </div>
            ))}
          </div>
        </div>

        {/* No Clips Message */}
        {!hasClips && (
          <div className="bg-accent/10 border border-accent/30 rounded-sm p-6 text-center" data-testid="no-clips-message">
            <VideoCamera weight="fill" className="w-12 h-12 text-accent mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg mb-2">Practice Videos Coming Soon</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              Practice clips can be added by the admin via the backoffice. 
              In the meantime, review the tips and hazard types above to prepare.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
